/**
 * Lead playbook — manual-assist CRM V1.
 *
 * Pure data: per-stage checklists and helpers. `TEMPLATES_BY_STAGE` was
 * the original source of truth for per-stage message bodies and is now
 * fallback-only for LeadTemplates when the backend catalog
 * (`message_templates`) is unreachable or empty. See
 * `hooks/useMessageTemplates.js` for the primary path.
 *
 * Everything else (`CHECKLIST_BY_STAGE`, stage labels, helpers) is still
 * the canonical source consumed across the CRM.
 */

const safe = (v, fallback = "") => (v && String(v).trim()) || fallback;

// suggestsNext: when a checklist item is toggled to done, show inline
// suggestion to update lead.next_action / next_action_date.
export const CHECKLIST_BY_STAGE = {
  new: [
    { key: "first_contact",   label: "Primer contacto realizado",
      suggestsNext: { action: "Llamar para cualificar", offsetDays: 1 } },
    { key: "qualified",       label: "Lead cualificado (presupuesto y plazos)" },
    { key: "assigned",        label: "Responsable asignado" },
  ],
  contacted: [
    { key: "discovery_done",  label: "Llamada de descubrimiento realizada",
      suggestsNext: { action: "Agendar call", offsetDays: 2 } },
    { key: "needs_logged",    label: "Necesidades anotadas" },
    { key: "call_proposed",   label: "Call propuesta" },
  ],
  meeting_scheduled: [
    { key: "call_confirmed",  label: "Call confirmada por el lead" },
    { key: "agenda_sent",     label: "Agenda enviada" },
    { key: "dossier_due_set", label: "Recordatorio de dossier programado",
      suggestsNext: { action: "Enviar dossier antes de la call", offsetDays: 0 } },
  ],
  dossier_pending: [
    { key: "dossier_prepared", label: "Dossier preparado / personalizado" },
    { key: "dossier_due_at_hand", label: "Hora del envío clara para todos",
      suggestsNext: { action: "Enviar dossier ahora", offsetDays: 0 } },
  ],
  dossier: [
    { key: "dossier_sent",     label: "Dossier enviado" },
    { key: "dossier_followup", label: "Seguimiento del dossier",
      suggestsNext: { action: "Confirmar recepción del dossier", offsetDays: 2 } },
    { key: "objections_done",  label: "Objeciones tratadas" },
  ],
  call_done: [
    { key: "call_summary",     label: "Resumen de la call registrado" },
    { key: "next_steps_set",   label: "Próximos pasos acordados",
      suggestsNext: { action: "Seguimiento post-call", offsetDays: 1 } },
  ],
  in_followup: [
    { key: "followup_24h",  label: "Primer seguimiento (24-48h)" },
    { key: "followup_week", label: "Segundo seguimiento (1 semana)",
      suggestsNext: { action: "Tercer seguimiento", offsetDays: 14 } },
    { key: "decision_pending", label: "Esperando decisión" },
  ],
  villa_on_hold: [
    { key: "hold_communicated", label: "Hold comunicado al lead" },
    { key: "hold_until_set",    label: "Fecha límite del hold acordada",
      suggestsNext: { action: "Confirmar reserva antes del hold", offsetDays: 5 } },
  ],
  reservation_sent: [
    { key: "reservation_sent",   label: "Contrato/reserva enviada" },
    { key: "reservation_confirmed", label: "Reserva confirmada por el lead",
      suggestsNext: { action: "Confirmar pago de reserva", offsetDays: 3 } },
  ],
  reserved: [
    { key: "deposit_received", label: "Reserva/depósito recibido" },
    { key: "contract_pending", label: "Contrato pendiente de firma",
      suggestsNext: { action: "Cerrar firma de contrato", offsetDays: 7 } },
  ],
  contract_signed: [
    { key: "contract_filed",   label: "Contrato firmado archivado" },
    { key: "ops_handoff",      label: "Handoff a operaciones" },
  ],
  closed_won: [
    { key: "investor_account_created", label: "Cuenta de inversor creada" },
    { key: "onboarding_complete",      label: "Onboarding completado" },
  ],
  lost: [
    { key: "reason_logged",     label: "Razón de pérdida registrada" },
    { key: "nurture_scheduled", label: "Reactivación futura programada",
      suggestsNext: { action: "Reactivar lead", offsetDays: 90 } },
  ],
};

// Fallback catalog used by LeadTemplates when the backend catalog is
// unavailable. Do NOT edit as a source of truth — the live catalog lives
// in Mongo (`message_templates`) and is managed from
// Admin → Plantillas de mensajes.
export const TEMPLATES_BY_STAGE = {
  new: [
    { key: "primer_contacto_wa", label: "Primer contacto (WhatsApp)", channel: "whatsapp",
      linkedChecklist: "first_contact",
      body: (l) => `Hola ${safe(l.name)}, soy del equipo de Dolce Villas Lombok. Vi tu interés en ${safe(l.interest, "nuestras villas")}. ¿Cuándo te vendría bien una llamada breve para contarte los detalles?` },
    { key: "primer_contacto_email", label: "Primer contacto (email)", channel: "email",
      body: (l) => `Hola ${safe(l.name)},\n\nGracias por tu interés en ${safe(l.interest, "Dolce Villas Lombok")}. Me gustaría agendar una breve llamada para entender tus prioridades y ayudarte con la información que necesites.\n\n¿Te encaja esta semana?\n\nUn saludo,` },
  ],
  contacted: [
    { key: "agendar_call_wa", label: "Proponer call (WhatsApp)", channel: "whatsapp",
      linkedChecklist: "call_proposed",
      body: (l) => `Hola ${safe(l.name)}, ¿te encaja una call de 20 min esta semana para revisar ${safe(l.interest, "la villa")} con calma? Te paso un par de huecos.` },
  ],
  meeting_scheduled: [
    { key: "recordatorio_call_wa", label: "Recordatorio de call", channel: "whatsapp",
      linkedChecklist: "call_confirmed",
      body: (l) => `Hola ${safe(l.name)}, te confirmo nuestra call. Unas horas antes te enviaré el dossier para que llegues con todo claro.` },
  ],
  dossier_pending: [
    { key: "dossier_preaviso_wa", label: "Aviso previo (WhatsApp)", channel: "whatsapp",
      body: (l) => `Hola ${safe(l.name)}, te paso ahora el dossier con todo el detalle de ${safe(l.interest, "la villa")} para que lo revises antes de nuestra call.` },
  ],
  dossier: [
    { key: "envio_dossier_wa", label: "Envío de dossier", channel: "whatsapp",
      linkedChecklist: "dossier_sent",
      body: (l) => `Hola ${safe(l.name)}, te paso el dossier completo de ${safe(l.interest, "la villa")}. Cuando lo revises hablamos y resolvemos cualquier duda.` },
    { key: "envio_dossier_email", label: "Envío dossier (email)", channel: "email",
      body: (l) => `Hola ${safe(l.name)},\n\nAdjunto el dossier completo de ${safe(l.interest, "la villa")}. Inclúyelo en tu revisión y quedamos para una llamada cuando lo hayas visto.\n\nUn saludo,` },
  ],
  call_done: [
    { key: "post_call_wa", label: "Resumen post-call (WhatsApp)", channel: "whatsapp",
      linkedChecklist: "call_summary",
      body: (l) => `Hola ${safe(l.name)}, gracias por la call. Como hablamos, te paso ahora los próximos pasos y quedamos atentos a tus comentarios.` },
  ],
  in_followup: [
    { key: "seguimiento_amable_wa", label: "Seguimiento amable", channel: "whatsapp",
      linkedChecklist: "followup_24h",
      body: (l) => `Hola ${safe(l.name)}, sin presión: ¿pudiste echar un vistazo al material que te envié? Si tienes dudas, las vemos cuando quieras.` },
  ],
  villa_on_hold: [
    { key: "hold_wa", label: "Confirmación de hold", channel: "whatsapp",
      linkedChecklist: "hold_communicated",
      body: (l) => `Hola ${safe(l.name)}, te hemos reservado ${safe(l.interest, "la villa")} con un hold interno. Confirmamos los próximos pasos para asegurar tu reserva.` },
  ],
  reservation_sent: [
    { key: "reserva_enviada_wa", label: "Reserva enviada (WhatsApp)", channel: "whatsapp",
      linkedChecklist: "reservation_sent",
      body: (l) => `Hola ${safe(l.name)}, te acabo de enviar la reserva de ${safe(l.interest, "tu villa")}. Quedo atento para resolver cualquier duda antes de confirmar.` },
  ],
  reserved: [
    { key: "bienvenida_reserva_wa", label: "Bienvenida tras reserva", channel: "whatsapp",
      body: (l) => `¡Enhorabuena ${safe(l.name)}! Reserva confirmada. En breve te llegará la información de tu portal de inversor y los próximos pasos.` },
  ],
  contract_signed: [
    { key: "contrato_firmado_email", label: "Onboarding tras firma (email)", channel: "email",
      body: (l) => `Hola ${safe(l.name)},\n\nGracias por firmar. Te damos la bienvenida formal a Dolce Villas Lombok. En las próximas 48h recibirás:\n\n- Acceso a tu portal de inversor\n- Calendario de pagos\n- Asignación de tu responsable de obra\n\nUn saludo,` },
  ],
  closed_won: [
    { key: "bienvenida_oficial_wa", label: "Bienvenida oficial", channel: "whatsapp",
      body: (l) => `Bienvenido oficialmente a la comunidad Dolce Villas Lombok, ${safe(l.name)}. Empezamos a trabajar contigo.` },
  ],
  lost: [
    { key: "reactivacion_wa", label: "Reactivación", channel: "whatsapp",
      body: (l) => `Hola ${safe(l.name)}, ha pasado un tiempo desde que hablamos. Tenemos novedades en Lombok que pueden encajar con lo que buscabas. ¿Te interesa que te ponga al día?` },
  ],
};

export const STAGE_LABEL = {
  new: "Nuevo lead",
  contacted: "Contactado",
  meeting_scheduled: "Call agendada",
  dossier_pending: "Dossier pendiente",
  dossier: "Dossier enviado",
  call_done: "Call realizada",
  in_followup: "Interés alto",
  villa_on_hold: "Villa en hold",
  reservation_sent: "Reserva enviada",
  reserved: "Reserva pagada",
  contract_signed: "Contrato firmado",
  closed_won: "Cerrado ganado",
  lost: "Cerrado perdido",
};

export const STAGE_ORDER = [
  "new","contacted","meeting_scheduled",
  "dossier_pending","dossier","call_done",
  "in_followup","villa_on_hold",
  "reservation_sent","reserved",
  "contract_signed","closed_won","lost",
];

export function buildWhatsappUrl(phone, body) {
  const digits = String(phone || "").replace(/\D+/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
}

export function offsetIsoDate(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + (Number(days) || 0));
  return d.toISOString().slice(0, 10);
}

export function findChecklistItem(stage, itemKey) {
  return (CHECKLIST_BY_STAGE[stage] || []).find(i => i.key === itemKey) || null;
}
